<template>
    <div class="pb2-doc-table">
        <table v-if="matrix.length">
            <tr v-for="(row, rowIndex) in matrix" :key="`r${rowIndex}-${row.length}`">
                <DocTableCell
                    v-for="(cell, colIndex) in row"
                    :key="`c${colIndex}`"
                    :tag="rowIndex === 0 ? 'th' : 'td'"
                    :text="stripMarkup(cell)"
                    @commit="commitCell(rowIndex, colIndex, $event)"
                    @menu="onCellMenu(rowIndex, colIndex, $event)"
                />
            </tr>
        </table>
        <button v-else type="button" class="pb2-doc-table__seed" contenteditable="false" @click="seed">
            {{ t('add_block') }}
        </button>
    </div>
</template>

<script setup lang="ts">
/**
 * Editable document-surface table over a Fields table matrix. Cells edit
 * in place (DocTableCell owns the contenteditable DOM); commits go through
 * the store with origin 'document'. Right-click on a cell opens the shared
 * block context menu with the row/column section.
 */
import { computed } from 'vue';
import DocTableCell from '@src/Common/Builder/Document/DocTableCell.vue';
import { useBuilderT } from '@src/Common/Builder/host';
import { normalizeMatrix, setCell } from '@src/Common/Builder/tableMatrix';
import { markerId } from '@src/Common/Builder/types';
import { useBlockContextMenuOptional } from '@src/Common/Builder/useBlockContextMenu';
import { useBuilderStore } from '@src/Common/Builder/useBuilderStore';
import type { BlockNode } from '@src/Common/Builder/types';

const props = withDefaults(defineProps<{ block: BlockNode; attr?: string }>(), { attr: 'content' });

const emit = defineEmits<{ settings: [] }>();

const t = useBuilderT();
const store = useBuilderStore();
const menu = useBlockContextMenuOptional();

const matrix = computed(() => normalizeMatrix(props.block.value?.[props.attr]));

/** Cells may carry markup from the panel's table field — text only here. */
const stripMarkup = (cell: string): string => {
    if (!cell.includes('<')) {
        return cell;
    }

    const template = document.createElement('template');
    template.innerHTML = cell;

    return template.content.textContent ?? '';
};

const commitCell = (row: number, col: number, text: string): void => {
    const id = markerId(props.block);

    if (id === null) {
        return;
    }

    store.updateValue(id, props.attr, setCell(matrix.value, row, col, text), 'document');
};

const seed = (): void => {
    const id = markerId(props.block);

    if (id !== null) {
        store.updateValue(
            id,
            props.attr,
            [
                ['', ''],
                ['', ''],
            ],
            'document',
        );
    }
};

const onCellMenu = (row: number, col: number, event: MouseEvent): void => {
    const id = markerId(props.block);

    if (menu === null || id === null) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    menu.open({ x: event.clientX, y: event.clientY }, id, {
        surface: 'document',
        tableCell: { attr: props.attr, row, col },
        openSettings: () => emit('settings'),
    });
};
</script>

<style scoped>
.pb2-doc-table table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.925em;
}

.pb2-doc-table :deep(.pb2-doc-table__cell) {
    padding: 0.3em 0.6em;
    text-align: left;
    border: 1px solid var(--pb2-border, #e4e4e7);
    outline: none;
}

.pb2-doc-table :deep(.pb2-doc-table__cell:focus) {
    box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--pb2-accent, #2563eb) 45%, transparent);
}

.pb2-doc-table :deep(th.pb2-doc-table__cell) {
    background: var(--pb2-surface-raised, #f4f4f5);
}

.pb2-doc-table__seed {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
    color: var(--pb2-muted, #71717a);
    background: var(--pb2-surface, #fff);
    border: 1px dashed var(--pb2-border, #e4e4e7);
    border-radius: var(--pb2-radius, 6px);
    cursor: pointer;
}
</style>
